import { LitElement, PropertyValues } from 'lit'
import { property } from 'lit/decorators.js'
import { redispatchEvent } from './redispatch.js'
import { requestSubmitPolyfill } from './polyfills.js'

requestSubmitPolyfill()

export class ChangeEvent extends Event {
  static type = 'change' as const
  value: string | number | boolean = ''

  constructor(value: string | number | boolean) {
    super(ChangeEvent.type, { bubbles: true, composed: true })
    this.value = value
  }
}


export class FormElement extends LitElement {
  static formAssociated = true
  // element-internals-polyfill types attachInternals() with a stale ElementInternals; the DOM lib type is the accurate one
  protected readonly internals = this.attachInternals() as unknown as ElementInternals
  protected initialValue: string | File = ''
  protected isInitialValueChange = true

  get form() {
    return this.internals.form
  }

  get type() {
    return this.localName
  }

  get validity() {
    return this.internals.validity
  }

  get validationMessage() {
    return this.internals.validationMessage
  }

  get willValidate() {
    return this.internals.willValidate
  }

  checkValidity() {
    return this.internals.checkValidity()
  }

  reportValidity() {
    return this.internals.reportValidity()
  }

  protected emit(value: string | number | boolean) {
    this.dispatchEvent(new ChangeEvent(value))
  }
}


export abstract class ValueFormElement extends FormElement {
  abstract name: string
  abstract value: string | File

  protected override updated(changedProperties: PropertyValues): void {
    if (changedProperties.has('value')) {
      this.internals.setFormValue(this.value)
    }

    super.updated(changedProperties)
  }
}


export abstract class BooleanFormElement extends FormElement {
  abstract name: string
  abstract value: string | File
  abstract checked: boolean

  protected override updated(changedProperties: PropertyValues): void {
    super.updated(changedProperties)

    if (changedProperties.has('checked')) {
      const value = this.value || 'on'
      this.internals.setFormValue(this.checked ? value : null)
    }
  }
}

export const buttonTypes = ['submit', 'reset', 'button'] as const
export type buttonTypesType = typeof buttonTypes

export class ButtonElement extends FormElement {
  @property({ type: String, attribute: 'type' })
  buttonType: buttonTypesType[number] = 'button'

  private isRedispatchingEvent = false

  protected dispatchSubmit(event: MouseEvent) {
    event.preventDefault()

    if (this.isRedispatchingEvent) {
      return
    }

    if (!this.form) {
      return
    }

    const isSubmit = this.buttonType === 'submit'
    const isReset = this.buttonType === 'reset'

    if (!(isSubmit || isReset)) {
      return
    }

    event.stopPropagation()
    this.isRedispatchingEvent = true
    const prevented = !redispatchEvent(this, event)
    this.isRedispatchingEvent = false
    if (prevented) {
      return
    }

    if (isSubmit && typeof this.form.requestSubmit == 'function') {
      this.form.requestSubmit()
      return
    }

    if (isSubmit) {
      this.form.submit()
      return
    }

    if (isReset) {
      this.form.reset()
      return
    }
  }
}
